import React from 'react'
import { Button, Checkbox, Dialog, EmptyState, Select } from '@/components/ds'
import { Ic } from '@/components/icons'
import { Avatar, PageSizeSelect, QueryState, Spinner, usePageSize } from '@/components/common'
import { listCampaigns, listQueue, queueCounts } from '@/lib/db'
import { cancelQueueItems, previewQueueItem, setQueuePaused } from '@/lib/api'
import { useAsync } from '@/lib/useAsync'
import { useToast } from '@/lib/toast'
import { relativeTime } from '@/lib/format'
import { useCountdown } from '@/lib/useCountdown'
import { mergeVars, renderString } from '@shared/render'
import { ACTIVE_QUEUE_STATUSES, type QueueItemExpanded, type QueueStatus } from '@shared/types'
import type { ScreenProps } from '@/App'

/** Historical default — the footer dropdown lets the user pick 25–250. */
const DEFAULT_PAGE_SIZE = 100

/**
 * The Queue is a WAITING ROOM of sends that are already on their way: nothing
 * here needs unlocking. sent / failed / canceled are terminal and live in Send
 * logs, so there are no tabs for them.
 */
const TABS = [
  { value: 'all', label: 'All', statuses: ACTIVE_QUEUE_STATUSES },
  { value: 'scheduled', label: 'Scheduled', statuses: ['scheduled'] },
  { value: 'paused', label: 'Paused', statuses: ['paused'] },
] as const

type Tab = (typeof TABS)[number]['value']

/** One-shot: set to a tab value before navigating here to arrive filtered. */
export const QUEUE_FILTER = 'lm-queue-filter'

const STATUS_PILL: Record<string, { bg: string; fg: string; label: string }> = {
  scheduled: { bg: '#dbe4ff', fg: '#274bcf', label: 'Scheduled' },
  paused: { bg: '#f7e3b0', fg: '#805209', label: 'Paused' },
}

const GRID = '26px 20px minmax(260px,1.7fr) 150px minmax(140px,0.9fr) 92px 84px'

export function QueueScreen({ narrow, onDataChange, onNavigate }: ScreenProps) {
  const toast = useToast()
  const [tab, setTab] = React.useState<Tab>(() => {
    const f = sessionStorage.getItem(QUEUE_FILTER)
    return f && TABS.some((t) => t.value === f) ? (f as Tab) : 'all'
  })
  React.useEffect(() => {
    sessionStorage.removeItem(QUEUE_FILTER)
  }, [])

  const [campaignId, setCampaignId] = React.useState('')
  const [page, setPage] = React.useState(0)
  const [selected, setSelected] = React.useState<Record<string, boolean>>({})
  const [canceling, setCanceling] = React.useState<QueueItemExpanded[] | null>(null)
  const [busy, setBusy] = React.useState(false)

  // LIVE VIEW: while there is scheduled work in the current view, the queue
  // and its counts silently re-poll every few seconds — so a row flips to
  // "sending now" when a tick claims it and leaves the tab when it sends,
  // without a manual refresh. Stale data stays rendered between polls (no
  // loading flash), nothing polls in a hidden tab, and an idle Sent view
  // holds no timer at all.
  const [liveWork, setLiveWork] = React.useState(false)
  const POLL_MS = 5000
  // The 5s poll re-runs the same closure, so it honours this size too.
  const [pageSize, setPageSize] = usePageSize('queue', DEFAULT_PAGE_SIZE)

  const campaigns = useAsync(() => listCampaigns(), [])
  const counts = useAsync(() => queueCounts(campaignId || undefined), [campaignId], {
    refreshEvery: POLL_MS,
    refreshActive: liveWork,
  })

  const statuses = TABS.find((t) => t.value === tab)!.statuses
  const queue = useAsync(
    () =>
      listQueue({
        statuses: statuses as unknown as QueueStatus[],
        campaignId: campaignId || undefined,
        limit: pageSize,
        offset: page * pageSize,
      }),
    [tab, campaignId, page, pageSize],
    { refreshEvery: POLL_MS, refreshActive: liveWork },
  )

  const rows = queue.data?.rows ?? []
  const total = queue.data?.count ?? 0
  const c = counts.data
  const activeTotal = (c?.scheduled ?? 0) + (c?.paused ?? 0)

  React.useEffect(() => {
    setLiveWork((c?.scheduled ?? 0) > 0 || rows.some((r) => r.status === 'scheduled'))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [counts.data, queue.data])

  const tabCount = (t: Tab) => (t === 'all' ? activeTotal : (c?.[t as QueueStatus] ?? 0))

  // Selections and paging reset when the view changes.
  React.useEffect(() => {
    setSelected({})
  }, [tab, campaignId, page])
  React.useEffect(() => {
    setPage(0)
  }, [tab, campaignId])

  const selectedIds = Object.keys(selected).filter((id) => selected[id])
  const selectedRows = rows.filter((r) => selected[r.id])
  const canPause = selectedRows.some((r) => r.status === 'scheduled')
  const canResume = selectedRows.some((r) => r.status === 'paused')

  function refresh() {
    queue.reload()
    counts.reload()
    onDataChange()
  }

  /** Hold or release rows. scheduled_at survives, so timing is preserved. */
  async function setPaused(ids: string[], paused: boolean) {
    if (!ids.length || busy) return
    setBusy(true)
    try {
      const result = await setQueuePaused(ids, paused)
      const when = result.rows[0]?.scheduled_at
      toast(
        paused ? 'info' : 'success',
        `${result.updated} email${result.updated === 1 ? '' : 's'} ${paused ? 'paused' : 'resumed'}`,
        paused
          ? 'Held in the queue until you resume it'
          : result.updated === 1 && when
            ? `Sends ${relativeTime(when)}`
            : 'Back on their original schedule',
      )
      setSelected({})
      refresh()
    } catch (e) {
      toast('error', paused ? 'Pause failed' : 'Resume failed', e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  async function cancel(items: QueueItemExpanded[]) {
    if (!items.length || busy) return
    setBusy(true)
    try {
      const result = await cancelQueueItems(items.map((i) => i.id))
      toast(
        'info',
        `${result.canceled} email${result.canceled === 1 ? '' : 's'} canceled`,
        'Removed from the queue — the lead stays on the campaign',
      )
      setSelected({})
      setCanceling(null)
      refresh()
    } catch (e) {
      toast('error', 'Cancel failed', e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  // ---- summary strip data ---------------------------------------------------
  const selectedCampaign = (campaigns.data ?? []).find((c2) => c2.id === campaignId)
  const scheduledTimes = rows
    .map((r) => r.scheduled_at)
    .filter((t): t is string => !!t)
    .sort()
  const earliest = scheduledTimes[0]
  const latest = scheduledTimes[scheduledTimes.length - 1]

  return (
    <div style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
      {/* toolbar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
          flex: 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          {TABS.map((t) => {
            const active = tab === t.value
            const n = tabCount(t.value)
            return (
              <button
                key={t.value}
                type="button"
                className="tap"
                onClick={() => setTab(t.value)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  border: 'none',
                  background: 'transparent',
                  padding: '2px 0',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-sans)',
                  fontSize: 13,
                  fontWeight: active ? 600 : 500,
                  color: active ? 'var(--accent-text)' : 'var(--text-muted)',
                  boxShadow: active ? 'inset 0 -2px 0 var(--accent)' : 'none',
                }}
              >
                {t.label}
                <span
                  className="tnum"
                  style={{
                    fontSize: 10.5,
                    fontWeight: 700,
                    padding: '0 6px',
                    borderRadius: 999,
                    background: active ? 'var(--accent-soft)' : 'var(--surface-sunken)',
                    color: active ? 'var(--accent-text)' : 'var(--text-muted)',
                  }}
                >
                  {n}
                </span>
              </button>
            )
          })}
        </div>

        <div style={{ width: 170 }}>
          <Select
            size="sm"
            options={[
              { value: '', label: 'All campaigns' },
              ...(campaigns.data ?? []).map((c2) => ({ value: c2.id, label: c2.name })),
            ]}
            value={campaignId}
            onChange={setCampaignId}
          />
        </div>

        <div style={{ flex: 1 }} />

        <button
          type="button"
          className="tap"
          onClick={() => onNavigate('logs')}
          style={{
            border: 'none',
            background: 'transparent',
            padding: 0,
            cursor: 'pointer',
            fontFamily: 'var(--font-sans)',
            fontSize: 12,
            color: 'var(--text-muted)',
          }}
        >
          Recently sent → Send logs
        </button>

        {selectedIds.length > 0 && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
              {selectedIds.length} selected
            </span>
            {canResume && (
              <Button
                size="sm"
                icon={<Ic.play size={14} />}
                disabled={busy}
                onClick={() =>
                  setPaused(
                    selectedRows.filter((r) => r.status === 'paused').map((r) => r.id),
                    false,
                  )
                }
              >
                Resume
              </Button>
            )}
            {canPause && (
              <Button
                size="sm"
                variant="secondary"
                icon={<Ic.pause size={14} />}
                disabled={busy}
                onClick={() =>
                  setPaused(
                    selectedRows.filter((r) => r.status === 'scheduled').map((r) => r.id),
                    true,
                  )
                }
              >
                Pause
              </Button>
            )}
            <Button
              size="sm"
              variant="secondary"
              icon={<Ic.ban size={14} />}
              disabled={busy}
              onClick={() => setCanceling(selectedRows)}
            >
              Cancel
            </Button>
          </div>
        )}
      </div>

      <QueryState
        loading={queue.loading}
        error={queue.error}
        empty={rows.length === 0}
        emptyTitle={tab === 'paused' ? 'Nothing on hold' : 'Queue is clear'}
        emptyDescription={
          tab === 'paused'
            ? 'Pause a scheduled email to park it here without canceling it.'
            : 'Leads schedule themselves as they arrive, and items leave the queue once they send — check Send logs for history.'
        }
        onRetry={refresh}
      >
        <div
          style={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid var(--glass-edge)',
            borderRadius: 'var(--glass-radius)',
            overflow: 'hidden',
            background: 'var(--surface-box)',
            boxShadow: 'var(--glass-shadow)',
          }}
          /* dense mail list — opaque so the rows stay legible over the blobs */
          className="lm-solid"
        >
          {/* summary strip */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '7px 12px',
              background: 'linear-gradient(90deg,#6f6ce6,#5b5bd6)',
              color: '#fff',
              flex: 'none',
              flexWrap: 'wrap',
            }}
          >
            <span style={{ fontSize: 13, fontWeight: 600 }}>
              {selectedCampaign ? selectedCampaign.name : 'All campaigns'}
            </span>
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                padding: '1px 8px',
                borderRadius: 999,
                background: 'rgba(255,255,255,.22)',
              }}
            >
              {total.toLocaleString()} {tab === 'all' ? 'in queue' : TABS.find((t) => t.value === tab)!.label.toLowerCase()}
            </span>
            <div style={{ flex: 1 }} />
            {earliest && (
              <span style={{ fontSize: 11.5, color: 'rgba(255,255,255,.82)' }}>
                earliest {relativeTime(earliest)} · latest {relativeTime(latest)}
              </span>
            )}
            <SenderSummary campaign={selectedCampaign} />
          </div>

          {/* header strip */}
          <div className="scroll-x" style={{ flex: 'none' }}>
            <div style={{ minWidth: 900 }}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: GRID,
                  gap: 10,
                  alignItems: 'center',
                  height: 30,
                  padding: '0 12px',
                  background: 'var(--surface-topbar)',
                  borderBottom: '1px solid var(--border-default)',
                  fontSize: 10.5,
                  fontWeight: 700,
                  color: '#6a6f86',
                  textTransform: 'uppercase',
                  letterSpacing: '.05em',
                }}
              >
                <span />
                <span />
                <span>Recipient · Subject</span>
                <span>Scheduled</span>
                <span>Campaign</span>
                <span>Status</span>
                <span />
              </div>
            </div>
          </div>

          {/* rows — only this scrolls */}
          <div className="scroll-x" style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            <div style={{ minWidth: 900 }}>
              {rows.map((row, i) => (
                <QueueRow
                  key={row.id}
                  row={row}
                  alt={i % 2 === 1}
                  busy={busy}
                  narrow={narrow}
                  checked={!!selected[row.id]}
                  onCheck={(v) => setSelected((s) => ({ ...s, [row.id]: v }))}
                  onPause={(paused) => setPaused([row.id], paused)}
                  onCancel={() => setCanceling([row])}
                />
              ))}
            </div>
          </div>

          {/* footer */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 10,
              padding: '6px 12px',
              background: 'var(--surface-box-header)',
              borderTop: '1px solid var(--border-default)',
              fontSize: 12,
              color: 'var(--text-muted)',
              flex: 'none',
            }}
          >
            <span style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <span>
                {total.toLocaleString()} in queue
                {total > 0 &&
                  ` · showing ${page * pageSize + 1}–${Math.min((page + 1) * pageSize, total)}`}
              </span>
              <PageSizeSelect
                value={pageSize}
                onChange={(n) => {
                  setPageSize(n)
                  setPage(0)
                }}
              />
            </span>
            <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <Button
                variant="ghost"
                size="sm"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                Previous
              </Button>
              <span
                className="tnum"
                style={{
                  fontSize: 11.5,
                  fontWeight: 600,
                  padding: '2px 9px',
                  borderRadius: 999,
                  background: 'var(--accent)',
                  color: 'var(--text-on-accent)',
                }}
              >
                {page + 1}
                {total > 0 ? ` / ${Math.max(1, Math.ceil(total / pageSize))}` : ''}
              </span>
              <Button
                variant="ghost"
                size="sm"
                disabled={(page + 1) * pageSize >= total}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </span>
          </div>
        </div>
      </QueryState>

      <Dialog
        open={!!canceling}
        onClose={() => setCanceling(null)}
        title={
          canceling && canceling.length > 1
            ? `Cancel ${canceling.length} emails?`
            : 'Cancel this email?'
        }
        footer={
          <>
            <Button variant="secondary" onClick={() => setCanceling(null)}>
              Keep it queued
            </Button>
            <Button variant="danger" disabled={busy} onClick={() => cancel(canceling ?? [])}>
              Cancel send
            </Button>
          </>
        }
      >
        Canceled emails never send and leave the queue — the record stays in Send logs. The lead
        remains on the campaign, so you can queue it again after editing the template. To hold one
        without canceling it, pause it instead.
      </Dialog>
    </div>
  )
}

// -----------------------------------------------------------------------------

/** from / reply-to for the summary strip, using the sender's own precedence. */
function SenderSummary({
  campaign,
}: {
  campaign?: { from_name: string | null; reply_to: string | null } | undefined
}) {
  if (!campaign) return null
  // Identity defaults are resolved server-side at send time; here we can only
  // show what the campaign itself overrides, so stay quiet when it sets neither.
  const from = campaign.from_name?.trim()
  const replyTo = campaign.reply_to?.trim()
  if (!from && !replyTo) return null
  return (
    <span style={{ fontSize: 11.5, color: 'rgba(255,255,255,.82)' }}>
      {from && <>from {from}</>}
      {from && replyTo && ' · '}
      {replyTo && <>reply-to {replyTo}</>}
    </span>
  )
}

function QueueRow({
  row,
  alt,
  busy,
  narrow,
  checked,
  onCheck,
  onPause,
  onCancel,
}: {
  row: QueueItemExpanded
  alt: boolean
  busy: boolean
  narrow: boolean
  checked: boolean
  onCheck: (v: boolean) => void
  onPause: (paused: boolean) => void
  onCancel: () => void
}) {
  const [hover, setHover] = React.useState(false)
  const [open, setOpen] = React.useState(false)

  const lead = row.leads
  const paused = row.status === 'paused'
  const pill = STATUS_PILL[row.status] ?? {
    bg: 'var(--pill-neutral-bg)',
    fg: 'var(--text-faint)',
    label: row.status,
  }

  // The SUBJECT IS MERGED for this recipient — same helpers the renderer uses,
  // so the collapsed line agrees with the expanded preview.
  const subject = lead
    ? renderString(row.templates?.subject ?? '', mergeVars(lead))
    : (row.templates?.subject ?? '(no template)')

  const when = row.scheduled_at
  const absolute = when
    ? new Date(when).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
    : null

  // Ticks by the second inside the final minute, then reads "sending…". A held
  // row shows "held" instead, so it is passed inactive and keeps no timer.
  const countdown = useCountdown(when, !paused && row.status === 'scheduled')
  const imminent = countdown.endsWith('s') || countdown === 'sending…'
  // A sender tick OWNS this row right now (claim released on completion or
  // after the 10-min TTL) — the honest in-flight state, from data the query
  // already selects.
  const sendingNow = !paused && row.status === 'scheduled' && !!row.claim_id

  return (
    <div style={{ borderBottom: '1px solid var(--border-row)' }}>
      <div
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'grid',
          gridTemplateColumns: GRID,
          gap: 10,
          alignItems: 'center',
          minHeight: 40,
          padding: '0 12px',
          cursor: 'pointer',
          background: checked
            ? 'var(--accent-soft)'
            : hover
              ? 'var(--surface-topbar)'
              : alt
                ? 'var(--surface-row-alt)'
                : 'transparent',
        }}
      >
        <span onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex' }}>
          <Checkbox checked={checked} onChange={onCheck} />
        </span>

        <span
          style={{
            display: 'inline-flex',
            color: 'var(--text-faint)',
            transform: open ? 'rotate(90deg)' : 'none',
            transition: 'transform var(--dur-fast) var(--ease-out)',
          }}
        >
          <Ic.chevronRight size={14} />
        </span>

        {/* recipient · subject */}
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <Avatar name={lead?.name || lead?.email || '?'} />
          <span
            style={{
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: 13,
            }}
          >
            <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
              {lead?.name || lead?.email || 'Unknown lead'}
            </span>{' '}
            <span
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 11.5,
                color: 'var(--text-muted)',
              }}
            >
              {lead?.email}
            </span>{' '}
            <span style={{ color: 'var(--text-faint)' }}>—</span>{' '}
            <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{subject}</span>
          </span>
        </span>

        {/* scheduled */}
        <span style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
          {when ? (
            <>
              <strong
                className={imminent && !paused ? 'tnum' : undefined}
                style={{
                  color:
                    (imminent || sendingNow) && !paused
                      ? 'var(--accent-text)'
                      : 'var(--text-secondary)',
                }}
              >
                {paused ? (
                  'held'
                ) : sendingNow ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <Ic.spinner size={12} className="animate-spin" />
                    sending now
                  </span>
                ) : row.status === 'scheduled' ? (
                  countdown
                ) : (
                  relativeTime(when)
                )}
              </strong>
              {absolute && ` · ${absolute}`}
            </>
          ) : (
            'next tick'
          )}
        </span>

        {/* campaign */}
        <span
          style={{
            fontSize: 12,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          <span style={{ color: 'var(--accent-text)', fontWeight: 500 }}>
            {row.campaigns?.name}
          </span>
        </span>

        {/* status */}
        <span
          style={{
            fontSize: 10.5,
            fontWeight: 700,
            padding: '1px 8px',
            borderRadius: 999,
            background: pill.bg,
            color: pill.fg,
            justifySelf: 'start',
            whiteSpace: 'nowrap',
          }}
        >
          {pill.label}
        </span>

        {/* actions */}
        <span
          onClick={(e) => e.stopPropagation()}
          style={{ display: 'inline-flex', gap: 2, justifySelf: 'end' }}
        >
          <RowAction
            label={paused ? 'Resume' : 'Pause'}
            tone={paused ? 'var(--status-approved-fg)' : 'var(--accent-text)'}
            disabled={busy}
            onClick={() => onPause(!paused)}
          >
            {paused ? <Ic.play size={14} /> : <Ic.pause size={14} />}
          </RowAction>
          <RowAction label="Cancel" tone="var(--danger-text)" disabled={busy} onClick={onCancel}>
            <Ic.ban size={14} />
          </RowAction>
        </span>
      </div>

      {open && <ExpandedEmail id={row.id} narrow={narrow} />}
    </div>
  )
}

function RowAction({
  label,
  tone,
  disabled,
  onClick,
  children,
}: {
  label: string
  tone: string
  disabled?: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  const [hover, setHover] = React.useState(false)
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 22,
        height: 22,
        border: 'none',
        borderRadius: 4,
        background: 'transparent',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        color: hover ? tone : 'var(--text-faint)',
        transition: 'color var(--dur-fast) var(--ease-out)',
      }}
    >
      {children}
    </button>
  )
}

/**
 * The full message, fetched on expand. Preview is the default — the rendered
 * email the recipient sees — with HTML source and plain text behind a toggle.
 */
function ExpandedEmail({ id, narrow }: { id: string; narrow: boolean }) {
  const preview = useAsync(() => previewQueueItem(id), [id])
  const [view, setView] = React.useState<'preview' | 'html' | 'text'>('preview')

  const tabBtn = (v: typeof view, label: string) => (
    <button
      key={v}
      type="button"
      onClick={() => setView(v)}
      style={{
        border: 'none',
        borderRadius: 4,
        padding: '2px 9px',
        cursor: 'pointer',
        fontFamily: 'var(--font-sans)',
        fontSize: 11.5,
        fontWeight: 600,
        background: view === v ? 'var(--surface-card)' : 'transparent',
        color: view === v ? 'var(--accent-text)' : 'var(--text-muted)',
      }}
    >
      {label}
    </button>
  )

  return (
    <div
      style={{
        background: 'var(--surface-preview)',
        borderTop: '1px solid var(--border-row)',
        padding: 14,
      }}
    >
      {preview.loading && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', color: 'var(--text-muted)', fontSize: 12.5 }}>
          <Spinner size={14} />
          Rendering…
        </div>
      )}

      {preview.error && (
        <EmptyState icon={<Ic.warning size={16} />} title="Couldn't render this" description={preview.error} />
      )}

      {preview.data && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              flexWrap: 'wrap',
              fontSize: 11.5,
              color: 'var(--text-muted)',
            }}
          >
            <span>
              From <strong style={{ color: 'var(--text-secondary)' }}>{preview.data.from}</strong>
            </span>
            <span>
              To <strong style={{ color: 'var(--text-secondary)' }}>{preview.data.to}</strong>
            </span>
            {preview.data.reply_to && (
              <span>
                Reply-to{' '}
                <strong style={{ color: 'var(--accent-text)' }}>{preview.data.reply_to}</strong>
              </span>
            )}
            <div style={{ flex: 1 }} />
            <span
              style={{
                display: 'inline-flex',
                gap: 2,
                padding: 2,
                borderRadius: 6,
                background: 'var(--surface-sunken)',
              }}
            >
              {tabBtn('preview', 'Preview')}
              {tabBtn('html', 'HTML')}
              {tabBtn('text', 'Plain text')}
            </span>
          </div>

          {view === 'preview' ? (
            <div
              style={{
                maxWidth: narrow ? '100%' : 640,
                background: '#fff',
                border: '1px solid var(--border-default)',
                borderRadius: 6,
                overflow: 'hidden',
              }}
            >
              <iframe
                title="Rendered email"
                sandbox=""
                srcDoc={preview.data.html}
                style={{ display: 'block', width: '100%', height: 420, border: 'none' }}
              />
            </div>
          ) : (
            <pre
              style={{
                margin: 0,
                padding: 12,
                maxHeight: 420,
                overflow: 'auto',
                background: 'var(--surface-card)',
                border: '1px solid var(--border-default)',
                borderRadius: 6,
                fontFamily: 'var(--font-mono)',
                fontSize: 11.5,
                lineHeight: 1.6,
                color: 'var(--text-primary)',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {view === 'html' ? preview.data.html : preview.data.text}
            </pre>
          )}
        </div>
      )}
    </div>
  )
}
